// src/utils/dateUtils.js

export function formatDateTimeForDisplay(date) {
  if (!date) return "";

  const utcDate = date.endsWith("Z") ? date : date + "Z";
  const d = new Date(utcDate);

  if (isNaN(d.getTime())) return "" ;

  return d.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Formats a date string or Date object into the 'YYYY-MM-DDTHH:mm' format
 * required by the datetime-local input. Returns an empty string if the date is invalid.
 * @param {string | Date} date - The date to format.
 * @returns {string} The formatted date string.
 */
export function formatDateTimeForInput(date) {
  if (!date) return "";
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return `${date}T00:00`;
  }
  // Create a new Date object to ensure consistency
  const d = new Date(date);
  // Check if the date is valid before trying to convert it
  if (isNaN(d.getTime())) return "";
  // Slicing the ISO string gives the correct local time format
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

/** Completa uma data local sem hora/minuto usando meia-noite. */
export function ensureLocalDateTime(value) {
  if (!value) return "";
  const text = String(value).trim();
  const match = text.match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{2})?(?::(\d{2}))?)?$/);
  if (!match) return text;
  return `${match[1]}T${match[2] || "00"}:${match[3] || "00"}`;
}

/** Converts a datetime-local value to the UTC ISO format expected by the API. */
export function localDateTimeToUtc(date) {
  if (!date) return null;
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export function formatDateTime(utcDate) {
  if (!utcDate) return "—";
  return new Date(utcDate).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// A API grava em UTC, mas devolve DateTime sem o "Z" ("2026-10-28T03:00:00"),
// e o Date do navegador leria isso como hora local. Acrescenta o "Z" quando
// não há fuso no texto.
export function asUtc(value) {
  if (typeof value !== "string") return value;
  return /(Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
}

// "agora", "há 5 min", "há 3 h", "ontem", "há 4 dias"; depois de uma semana,
// a data.
export function formatRelative(utcDate) {
  if (!utcDate) return "";
  const date = new Date(asUtc(utcDate));
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "ontem";
  if (days < 7) return `há ${days} dias`;
  return formatDate(date);
}

export function formatDate(utcDate) {
  if (!utcDate) return "—";
  return new Date(utcDate).toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
